import api from './api';

/**
 * سرویس «مرکز دیجی‌پی» — آینهٔ `figano-backend/src/routes/admin/digipay.routes.ts`
 * (apiService پاسخ `{status, data}` را باز می‌کند و مستقیماً `data` را برمی‌گرداند)
 */

export type DigipayInstrument = 'ipg' | 'credit' | 'wallet';

export const DP_INSTRUMENT_LABELS: Record<string, string> = {
  ipg: 'کارت بانکی (IPG)',
  credit: 'خرید اقساطی / اعتباری',
  wallet: 'کیف پول دیجی‌پی',
  unknown: 'نامشخص',
};

export const DP_INSTRUMENT_COLORS: Record<string, string> = {
  ipg: '#2563EB',
  credit: '#9333EA',
  wallet: '#0EA5E9',
  unknown: '#64748B',
};

export const DP_TICKET_TYPE_LABELS: Record<number, string> = {
  0: 'IPG (درگاه بانکی)',
  5: 'CREDIT (اعتبار خرید)',
  11: 'WALLET (کیف پول)',
  13: 'BNPL (خرید اقساطی)',
  24: 'CREDIT-CARD (کارت اعتباری)',
};

export const DP_REFUND_STATUS_LABELS: Record<number, string> = { 0: 'موفق', 1: 'ناموفق', 2: 'نامشخص / در جریان' };
export const DP_REFUND_STATUS_COLORS: Record<number, string> = { 0: '#16A34A', 1: '#DC2626', 2: '#D97706' };
export const DP_DESTINATION_LABELS: Record<number, string> = { 0: 'کارت (Masked PAN)', 1: 'شبا (IBAN)', 2: 'کیف پول دیجی‌پی', 3: 'اعتبار' };

export interface DigipaySettingsBundle {
  /** تنظیم «در استرداد، کارمزد اقساطی هم به خریدار برگردد» (پیش‌فرض دیالوگ‌های reverse/refund) */
  refundIncludesSurcharge?: boolean;
  enabled: boolean;
  allowCredit: boolean;
  allowWallet: boolean;
  creditSurchargePercent: number;
  ipgSurchargePercent: number;
  surchargeMode: 'on_base' | 'gross_up';
  creditPreferredGateway: string;
  creditOrdersOnly: boolean;
  basketCategoryId: string;
  basketProductType: number;
}

export interface DigipayOverview {
  configured: boolean;
  apiBase: string;
  environment: 'staging' | 'production';
  amountUnit: string;
  version: string;
  callbackUrl: string;
  clientIdHasNonAscii: boolean;
  settings: DigipaySettingsBundle;
  counts: Record<string, { count: number; amount: number; surcharge: number }>;
  total: number;
  pendingDeliver: number;
  deliverErrors: number;
  refunds: Record<string, { count: number; amount: number }>;
  surchargeByStatus: Record<string, { count: number; amount: number }>;
  paidLast24h: number;
  attempts24h: Record<string, number>;
  instruments: string[];
}

export interface DigipayRefundRecord {
  providerId: string;
  trackingCode?: string;
  amount: number;
  reason?: string;
  status: number;
  resultCode?: number;
  transferDate?: string;
  destinationType?: number;
  destination?: string;
  adminId?: string;
  createdAt: string;
  checkedAt?: string;
  error?: string;
}

export interface DigipayOrderRow {
  _id: string;
  orderNumber: string;
  status: string;
  paymentStatus: string;
  createdAt: string;
  user: { _id: string; name?: string; phone?: string; email?: string } | null;
  totalAmount: number;
  payableAmount: number | null;
  paidAmount: number | null;
  surcharge: { amount: number; ratePercent: number; mode: string; status: string; instrument?: string; actualInstrument?: string; adjustedAmount?: number } | null;
  instrument: string | null;
  ticketType: number | null;
  isCredit: boolean;
  trackingCode: string | null;
  providerId: string | null;
  rrn: string | null;
  maskedPan: string | null;
  pspCode: string | null;
  pspName: string | null;
  terminalId: string | null;
  paidAt: string | null;
  verifiedAt: string | null;
  reverseDeadline: string | null;
  reverseWindowOpen: boolean;
  digipayReversedAt: string | null;
  deliverReportedAt: string | null;
  deliverError: string | null;
  refunds: DigipayRefundRecord[];
  refundedViaDigipay: number;
  refundedTotal: number;
}

export interface Paged<T> {
  docs: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DigipayOrderItem {
  _id: string;
  productTitle: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  discountAmount?: number;
  status: string;
  refundedAmount?: number;
  refundedQuantity?: number;
  storeName?: string;
  fulfillmentType?: string;
}

export interface DigipayLog {
  _id: string;
  action: string;
  status: string;
  amount?: number;
  authority?: string;
  refId?: string;
  code?: number;
  message?: string;
  error?: string;
  meta?: any;
  requestBody?: any;
  responseBody?: any;
  callbackQuery?: any;
  createdAt: string;
  orderId?: { _id: string; orderNumber: string; totalAmount: number } | string | null;
  userId?: { _id: string; name?: string; phone?: string } | string | null;
}

export interface DigipayOrderDetail {
  order: DigipayOrderRow;
  items: DigipayOrderItem[];
  activeItemsSum: number;
  logs: DigipayLog[];
  attemptOnly?: boolean;
  attemptNote?: string;
  maxRefundable?: number;
  capabilities: { canReverse: boolean; canRefund: boolean; canDeliver: boolean; canRefundSurcharge: boolean; canInquireRefund?: boolean };
}

export interface DigipayRefundListRow {
  orderId: string;
  orderNumber: string;
  userId: string | null;
  totalAmount: number;
  paymentStatus: string;
  saleTrackingCode?: string;
  instrument?: string;
  ticketType?: number;
  refund: DigipayRefundRecord;
}

export interface DigipayRefundList extends Paged<DigipayRefundListRow> {
  reversed: Array<{ orderId: string; orderNumber: string; amount: number; reversedAt: string; trackingCode?: string }>;
}

/** 🧪 تیکت آزمایشی روی سرور واقعی (بدون پرداخت) */
export interface DigipayProbeTicket {
  ok: boolean;
  providerId: string;
  instrument: DigipayInstrument;
  amountToman: number;
  amountSent: number;
  amountUnit: 'rial' | 'toman';
  ticket?: string;
  redirectUrl?: string;
  apiBase?: string;
  ms: number;
  hint?: string;
  error?: string;
  httpStatus?: number;
  resultStatus?: number;
  raw?: any;
}

export interface DigipayConnectionTest {
  apiBase: string;
  host: string;
  configured: boolean;
  amountUnit?: string;
  clientIdHasNonAscii?: boolean;
  dns: { ok: boolean; addresses: string[]; error?: string };
  login: { ok: boolean; error?: string; ms: number };
  hint: string;
  /** تشخیص env سرور: کوتیشن دورِ مقدار، فاصله، طول مؤثر و مقدار ماسک‌شده */
  credentials?: Array<{ key: string; present: boolean; rawLength: number; effectiveLength: number; quotedInEnv: boolean; hasWhitespace: boolean; masked: string }>;
  envWarnings?: string[];
}

export interface DigipayQuote {
  amount: number;
  instrument: DigipayInstrument;
  ratePercent: number;
  mode: 'on_base' | 'gross_up';
  surchargeAmount: number;
  payableAmount: number;
  gatewayAmount: number;
  amountUnit: string;
  preferredGateway: number | null;
  basketRequired: boolean;
  available: boolean;
}

export interface RefundInquiryResult {
  success: boolean;
  refundStatus?: 0 | 1 | 2;
  resultCode?: number;
  transferDate?: string;
  destinationType?: number;
  destination?: string;
  message: string;
  raw?: any;
}

const BASE = '/admin/digipay';

const digipayAdminService = {
  overview: () => api.get<DigipayOverview>(`${BASE}/overview`),

  listOrders: (params: { page?: number; limit?: number; instrument?: string; filter?: string; search?: string; from?: string; to?: string }) =>
    api.get<Paged<DigipayOrderRow>>(`${BASE}/orders`, {
      params: {
        page: params.page || 1,
        limit: params.limit || 20,
        instrument: params.instrument || undefined,
        filter: params.filter || undefined,
        search: params.search || undefined,
        from: params.from || undefined,
        to: params.to || undefined,
      },
    }),

  orderDetail: (orderId: string) => api.get<DigipayOrderDetail>(`${BASE}/orders/${orderId}`),
  orderReverse: (orderId: string, reason?: string, includeSurcharge?: boolean) => api.post<any>(`${BASE}/orders/${orderId}/reverse`, { reason, includeSurcharge }),
  orderRefund: (orderId: string, amount?: number, reason?: string, includeSurcharge?: boolean) => api.post<any>(`${BASE}/orders/${orderId}/refund`, { amount, reason, includeSurcharge }),
  orderDeliver: (orderId: string) => api.post<any>(`${BASE}/orders/${orderId}/deliver`, {}),
  orderRefundSurcharge: (orderId: string, reason?: string) => api.post<any>(`${BASE}/orders/${orderId}/refund-surcharge`, { reason }),
  refundStatus: (orderId: string, inquiryId: string) => api.get<RefundInquiryResult>(`${BASE}/orders/${orderId}/refunds/${encodeURIComponent(inquiryId)}`),

  listRefunds: (params: { page?: number; limit?: number; status?: string }) =>
    api.get<DigipayRefundList>(`${BASE}/refunds`, { params: { page: params.page || 1, limit: params.limit || 25, status: params.status ?? undefined } }),

  listLogs: (params: { page?: number; limit?: number; action?: string; status?: string; search?: string; orderId?: string }) =>
    api.get<Paged<DigipayLog>>(`${BASE}/logs`, {
      params: {
        page: params.page || 1,
        limit: params.limit || 25,
        action: params.action || undefined,
        status: params.status || undefined,
        search: params.search || undefined,
        orderId: params.orderId || undefined,
      },
    }),

  testConnection: () => api.get<DigipayConnectionTest>(`${BASE}/tools/test-connection`),
  quote: (amount: number, instrument: DigipayInstrument) => api.post<DigipayQuote>(`${BASE}/tools/quote`, { amount, instrument }),
  probeTicket: (cell: string, instrument: DigipayInstrument, amount = 1000) => api.post<DigipayProbeTicket>(`${BASE}/tools/probe-ticket`, { cell, instrument, amount }),
  sweepDeliver: (limit = 50) => api.post<any>(`${BASE}/tools/sweep-deliver`, { limit }),
  refreshRefunds: (limit = 50) => api.post<any>(`${BASE}/tools/refresh-refunds`, { limit }),
};

export default digipayAdminService;

/** جدول کدهای پاسخ رسمی دیجی‌پی (مستند UPG §۱۲ — بدنهٔ خطای 422، فیلد result.status) + اقدام پیشنهادی */
export const DP_RESULT_CODES: Array<{ code: number; message: string; action: string }> = [
  { code: 0, message: 'عملیات با موفقیت انجام شد', action: '—' },
  { code: 1054, message: 'اطلاعات ورودی اشتباه است', action: 'پارامترهای درخواست را با مستند تطبیق دهید (شماره موبایل، مبلغ، providerId، callbackUrl، basketDetailsDto برای اعتباری).' },
  { code: 9000, message: 'اطلاعات خرید یافت نشد', action: 'trackingCode/providerId اشتباه است یا خرید در دیجی‌پی وجود ندارد. از لاگ درخواست همان سفارش کپی کنید.' },
  { code: 9001, message: 'توکن پرداخت معتبر نیست', action: 'تیکت منقضی/نامعتبر است. خریدار باید دوباره پرداخت را شروع کند.' },
  { code: 9003, message: 'خرید مورد نظر منقضی شده است', action: 'مهلت پرداخت تیکت گذشته؛ سفارش pending با درگاه دیگر یا تیکت جدید پرداخت شود.' },
  { code: 9004, message: 'خرید مورد نظر در حال انجام است', action: 'چند ثانیه صبر کنید و دوباره تلاش کنید؛ درخواست موازی نزنید.' },
  { code: 9005, message: 'خرید قابل پرداخت نیست', action: 'وضعیت خرید در دیجی‌پی اجازهٔ پرداخت نمی‌دهد. تیکت جدید بسازید.' },
  { code: 9006, message: 'خطا در برقراری ارتباط با درگاه پرداخت', action: 'مشکل سمت PSP/دیجی‌پی. بعداً تکرار کنید؛ اگر ادامه داشت با پشتیبانی دیجی‌پی تماس بگیرید.' },
  { code: 9007, message: 'خرید با موفقیت انجام نشده است', action: 'پرداخت ناموفق بوده؛ verify معنا ندارد. سفارش ناموفق ثبت می‌شود.' },
  { code: 9008, message: 'این خرید با داده‌های متفاوتی قبلاً ثبت شده است', action: 'providerId تکراری با دادهٔ متفاوت. سیستم برای هر تلاش providerId یکتا می‌سازد؛ اگر دیدید، لاگ درخواست را بررسی کنید.' },
  { code: 9009, message: 'محدودهٔ زمانی تأیید تراکنش گذشته است', action: 'verify دیر انجام شده و دیجی‌پی پول را خودکار به خریدار برگردانده. سفارش پرداخت‌نشده تلقی می‌شود؛ خریدار دوباره پرداخت کند.' },
  { code: 9010, message: 'تأیید خرید ناموفق بود', action: 'verify رد شد؛ مبلغ نزد دیجی‌پی برمی‌گردد. اگر خریدار مدعی کسر وجه است با کد پیگیری به پشتیبانی دیجی‌پی ارجاع دهید.' },
  { code: 9011, message: 'نتیجهٔ تأیید خرید نامشخص است', action: 'سیستم خودکار ۳ بار verify را تکرار می‌کند. اگر باز نامشخص ماند سفارش pending می‌ماند؛ از لاگ‌ها وضعیت را پیگیری و در صورت نیاز با پشتیبانی دیجی‌پی هماهنگ کنید.' },
  { code: 9012, message: 'وضعیت خرید برای این درخواست صحیح نیست', action: 'مثلاً refund/deliver روی خریدی که verify نشده یا قبلاً reverse شده. وضعیت فعلی خرید را از لاگ‌ها ببینید.' },
  { code: 9030, message: 'ورود شمارهٔ همراه برای کاربران ثبت‌نام‌شده الزامی است', action: 'cellNumber خالی/نامعتبر بوده. شمارهٔ موبایل خریدار در حساب کاربری باید ۱۱ رقمی با 09 باشد.' },
  { code: 9031, message: 'اعطای تیکت برای کاربر مورد نظر امکان‌پذیر نیست', action: 'دیجی‌پی برای این شمارهٔ موبایل تیکت نمی‌دهد (محدودیت کاربر). خریدار با درگاه دیگری پرداخت کند.' },
];

export const DP_HTTP_CODES: Array<{ code: string; meaning: string }> = [
  { code: '200', meaning: 'موفق' },
  { code: '400', meaning: 'پارامترهای ورودی نامعتبر است' },
  { code: '401 / 403', meaning: 'خطا در احراز هویت و دسترسی (توکن منقضی/نامعتبر، اعتبارنامهٔ غلط)' },
  { code: '422', meaning: 'خطای بیزینسی — کد در result.status (جدول بالا)' },
  { code: '500', meaning: 'خطای داخلی دیجی‌پی' },
];

export const DP_PSP_CODES: Array<{ code: string; name: string }> = [
  { code: '001', name: 'سامان کیش' }, { code: '002', name: 'تجارت الکترونیک پارسیان' }, { code: '003', name: 'به‌پرداخت ملت' },
  { code: '004', name: 'پرداخت نوین آرین' }, { code: '005', name: 'پرداخت الکترونیک پاسارگاد' }, { code: '006', name: 'فن‌آوا' },
  { code: '007', name: 'سداد (ملی)' }, { code: '008', name: 'ایران‌کیش' }, { code: '009', name: 'پاد' },
];

/** پیش‌نمایش استرداد سرور («دقیقاً چه اتفاقی می‌افتد») — مشترک سفارش‌ها / مرکز دیجی‌پی / مرکز اسنپ‌پی */
export const adminRefundPreviewService = {
  order: (orderId: string, params: { amount?: number; includeSurcharge?: boolean }) =>
    api.get<import('../components/common/RefundPreviewSteps').RefundPreviewData>(`/admin/orders/${orderId}/refund-preview`, { params }),
  item: (orderItemId: string, params: { amount?: number; includeSurcharge?: boolean }) =>
    api.get<import('../components/common/RefundPreviewSteps').RefundPreviewData>(`/admin/orders/items/${orderItemId}/refund-preview`, { params }),
};
